import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { PAGE_COLORS } from '../../utils/pageColors';
import { useGetQuotesQuery, useDeleteQuoteMutation, useUpdateQuoteFlagsMutation, useSendQuoteEmailMutation } from '../../store/slices/apiSlice';
import { FileText, Plus, Eye, Edit, Trash2, Search, Mail } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { ToggleSwitch } from '../../components/quotes/ToggleSwitch';

export const QuotesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { data, isLoading, error } = useGetQuotesQuery({ 
    page, 
    search, 
    page_size: 20,
    status: statusFilter !== 'all' ? statusFilter : undefined 
  });
  const [deleteQuote] = useDeleteQuoteMutation();
  const [updateFlags] = useUpdateQuoteFlagsMutation();
  const [sendQuoteEmail] = useSendQuoteEmailMutation();
  const [sendingQuoteId, setSendingQuoteId] = useState<string | null>(null);
  const toast = useToast();

  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter);
    setPage(1); // Reset to first page when filter changes
  };

  const handleDelete = async (id: string, quoteNumber: string) => {
    if (!confirm(`Delete quote ${quoteNumber}? This cannot be undone.`)) return;
    
    try {
      await deleteQuote(id).unwrap();
      toast.success('Success', 'Quote deleted successfully');
    } catch (error) {
      toast.error('Error', 'Failed to delete quote');
    }
  };

  const handlePaidToggle = async (quoteId: string, currentValue: boolean, canToggle: boolean) => {
    if (!canToggle) {
      toast.warning('Warning', 'PDF must be generated before marking as paid');
      return;
    }
    
    try {
      await updateFlags({ id: quoteId, is_paid: !currentValue }).unwrap();
      toast.success('Success', currentValue ? 'Marked as unpaid' : 'Marked as paid');
    } catch (error) {
      toast.error('Error', 'Failed to update paid status');
    }
  };

  const handleJobAddedToggle = async (quoteId: string, currentValue: boolean, canToggle: boolean) => {
    if (!canToggle) {
      toast.warning('Warning', 'PDF must be generated before marking as job added');
      return;
    }
    
    try {
      await updateFlags({ id: quoteId, is_job_added: !currentValue }).unwrap();
      toast.success('Success', currentValue ? 'Marked as job not added' : 'Marked as job added');
    } catch (error) {
      toast.error('Error', 'Failed to update job added status');
    }
  };

  const handleSendQuoteEmail = async (quoteId: string) => {
    setSendingQuoteId(quoteId);
    try {
      await sendQuoteEmail(quoteId).unwrap();
      toast.success('Success', 'Quote email queued for sending');
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err && typeof (err as { data?: { error?: string } }).data?.error === 'string'
        ? (err as { data: { error: string } }).data.error
        : 'Failed to send quote email';
      toast.error('Error', message);
    } finally {
      setSendingQuoteId(null);
    }
  };

  return (
    <AppShell>
      <PageLayout
        title="Quotes"
        subtitle="Manage customer quotations"
        bgColor={PAGE_COLORS.orders.bgColor}
        textColor={PAGE_COLORS.orders.textColor}
        borderColor={PAGE_COLORS.orders.borderColor}
        icon={<FileText className="w-6 h-6" />}
      >
        {/* Status Filter */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleFilterChange('draft')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'draft'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Draft
            </button>
            <button
              onClick={() => handleFilterChange('pdf_generated')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'pdf_generated'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              PDF Generated
            </button>
            <button
              onClick={() => handleFilterChange('paid')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'paid'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Paid
            </button>
            <button
              onClick={() => handleFilterChange('job_added')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'job_added'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Job Added
            </button>
          </div>
        </div>

        {/* Header with search and create button */}
        <div className="mb-6 flex justify-between items-center gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by quote number, customer name, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <Link to="/quotes/new">
            <Button variant="primary" icon={<Plus className="w-4 h-4" />}>
              Create New Quote
            </Button>
          </Link>
        </div>

        <ContentCard>
          {isLoading && (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading quotes...</p>
            </div>
          )}
          
          {error && (
            <div className="text-center py-8">
              <p className="text-red-600">Error loading quotes. Please try again.</p>
            </div>
          )}
          
          {data && data.results.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No quotes found</p>
              <Link to="/quotes/new">
                <Button variant="primary">Create Your First Quote</Button>
              </Link>
            </div>
          )}
          
          {data && data.results.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left p-3 font-semibold text-gray-700">Quote Number</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Customer</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Total Amount</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Status</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Paid</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Job Added</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Email Sent</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Created</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((quote) => (
                      <tr key={quote.id} className={`border-b transition-colors ${
                        quote.status === 'job_added' ? 'bg-purple-50 hover:bg-purple-100' :
                        quote.status === 'paid' ? 'bg-green-50 hover:bg-green-100' :
                        quote.status === 'pdf_generated' ? 'bg-blue-50 hover:bg-blue-100' :
                        'bg-yellow-50 hover:bg-yellow-100'
                      }`}>
                        <td className="p-3">
                          <span className="font-mono font-medium text-blue-600">
                            {quote.quote_number}
                          </span>
                        </td>
                        <td className="p-3">
                          <div>
                            <div className="font-medium">
                              {quote.customer_first_name} {quote.customer_last_name}
                            </div>
                            {quote.customer_business_name && (
                              <div className="text-sm text-gray-600">{quote.customer_business_name}</div>
                            )}
                            {quote.customer_email && (
                              <div className="text-sm text-gray-500">{quote.customer_email}</div>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <span className="font-semibold text-gray-900">
                            £{Number(quote.total_amount).toFixed(2)}
                          </span>
                          <div className="text-xs text-gray-500">
                            VAT: £{Number(quote.vat_total).toFixed(2)}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            quote.status === 'job_added' ? 'bg-purple-100 text-purple-800' :
                            quote.status === 'paid' ? 'bg-green-100 text-green-800' :
                            quote.status === 'pdf_generated' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {quote.status === 'job_added' ? 'Job Added' :
                             quote.status === 'paid' ? 'Paid' :
                             quote.status === 'pdf_generated' ? 'PDF Generated' : 'Draft'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <ToggleSwitch
                            checked={quote.is_paid}
                            onChange={() => handlePaidToggle(quote.id, quote.is_paid, quote.status !== 'draft')}
                            disabled={quote.status === 'draft'}
                          />
                        </td>
                        <td className="p-3 text-center">
                          <ToggleSwitch
                            checked={quote.is_job_added}
                            onChange={() => handleJobAddedToggle(quote.id, quote.is_job_added, quote.status !== 'draft')}
                            disabled={quote.status === 'draft'}
                          />
                        </td>
                        <td className="p-3">
                          {quote.quote_email_sent_at ? (
                            <div className="text-sm">
                              {new Date(quote.quote_email_sent_at).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                              <div className="text-xs text-gray-500">
                                {new Date(quote.quote_email_sent_at).toLocaleTimeString('en-GB', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            {new Date(quote.created_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(quote.created_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-2 flex-wrap">
                            {quote.status !== 'draft' && quote.pdf_url && quote.customer_email && (
                              <button
                                type="button"
                                onClick={() => handleSendQuoteEmail(quote.id)}
                                disabled={sendingQuoteId === quote.id}
                                className="inline-flex items-center px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:pointer-events-none"
                                title={quote.quote_email_sent_at ? 'Resend quote email' : 'Send quote email'}
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                {sendingQuoteId === quote.id
                                  ? 'Sending…'
                                  : quote.quote_email_sent_at
                                    ? 'Resend Email'
                                    : 'Send Email'}
                              </button>
                            )}
                            {quote.pdf_url && (
                              <a
                                href={quote.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                                title="View PDF"
                              >
                                <Eye className="w-4 h-4 mr-1" /> View PDF
                              </a>
                            )}
                            <Link
                              to={`/quotes/${quote.id}/edit`}
                              className="inline-flex items-center px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                              title="Edit quote"
                            >
                              <Edit className="w-4 h-4 mr-1" /> Edit
                            </Link>
                            <button
                              onClick={() => handleDelete(quote.id, quote.quote_number)}
                              className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                              title="Delete quote"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data.count > 20 && (
                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.count)} of {data.count} quotes
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setPage(page - 1)}
                      disabled={!data.previous}
                      variant="secondary"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setPage(page + 1)}
                      disabled={!data.next}
                      variant="secondary"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
};
